/**
 * Environment variable validation for runtime checks
 * Validates required Supabase configuration before app initialization
 */

export class EnvValidationError extends Error {
  constructor(public missingVars: string[]) {
    super(`Missing or invalid environment variables: ${missingVars.join(', ')}`);
    this.name = 'EnvValidationError';
  }
}

interface ValidatedEnv {
  supabaseUrl: string;
  supabaseKey: string;
}

/**
 * Validates that all required environment variables are set and valid
 * @throws EnvValidationError if validation fails
 * @returns Validated environment values
 */
export function validateEnv(): ValidatedEnv {
  const errors: string[] = [];
  
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  
  // Validate VITE_SUPABASE_URL
  if (!supabaseUrl) {
    errors.push('VITE_SUPABASE_URL (puuttuu)');
  } else {
    try {
      const url = new URL(supabaseUrl);
      if (url.protocol !== 'https:') {
        errors.push('VITE_SUPABASE_URL (virheellinen protokolla, vaaditaan https)');
      }
    } catch {
      errors.push('VITE_SUPABASE_URL (virheellinen URL-muoto)');
    }
  }
  
  // Validate VITE_SUPABASE_PUBLISHABLE_KEY
  if (!supabaseKey) {
    errors.push('VITE_SUPABASE_PUBLISHABLE_KEY (puuttuu)');
  } else if (!supabaseKey.startsWith('eyJ')) {
    errors.push('VITE_SUPABASE_PUBLISHABLE_KEY (virheellinen JWT-muoto)');
  }
  
  if (errors.length > 0) {
    throw new EnvValidationError(errors);
  }
  
  return { supabaseUrl, supabaseKey };
}
